'use client';

import { memo, useRef } from 'react';
import Image from 'next/image';
import moment from 'moment';
import clsx from 'clsx';

import { Note, removeNoteApi, useLessonStore } from '@entities/lesson';
import CloseIcon from '@public/icons/close.svg';

import styles from './Note.module.scss';

interface NoteComponentProps {
  note: Note;
  onClick?: (id: string) => void;
}

const NoteComponent = memo((props: NoteComponentProps) => {
  const { note, onClick } = props;

  const activeNoteId = useLessonStore((state) => state.activeNoteId);
  const removeNote = useLessonStore((state) => state.removeNote);
  const setActiveNoteId = useLessonStore((state) => state.setActiveNoteId);
  const setSectionId = useLessonStore((state) => state.setSectionId);

  const isLoading = useRef<boolean>(false);

  const remove = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isLoading.current) {
      return;
    }

    isLoading.current = true;

    removeNoteApi({ id: note.id })
      .then(() => {
        removeNote(note.id);

        if (activeNoteId === note.id) {
          setActiveNoteId(undefined);
        }
      })
      .finally(() => {
        isLoading.current = false;
      });
  };

  const handleClick = () => {
    setActiveNoteId(note.id);
    onClick?.(note.id);

    if (note.anchor) {
      setSectionId(note.anchor.sectionId);
    }
  };

  return (
    <div
      id={`note-${note.id}`}
      className={clsx(styles.container, activeNoteId === note.id && styles.container_active)}
      onClick={handleClick}
    >
      <div className={styles.header}>
        <div className={styles.timestamp}>{moment(note.createdAt).format('D MMM HH:mm:ss')}</div>

        <Image
          src={CloseIcon}
          className={styles.remove}
          alt="Remove"
          draggable={false}
          onClick={remove}
        />
      </div>

      <div className={styles.content}>{note.note}</div>
    </div>
  );
});

export default NoteComponent;
